/**
 * Runtime bridge to the Tauri host. All calls are guarded behind
 * `isTauri()` — in a browser context these imports still resolve but
 * the functions do nothing, so the hosted app at app.remedy.build and
 * the desktop build share a single codebase.
 *
 * Dynamic imports keep the @tauri-apps/* code out of the browser bundle:
 * Vite creates a separate chunk that's only requested when isTauri()
 * returns true at runtime.
 */

export function isTauri(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('__TAURI_INTERNALS__' in window || '__TAURI__' in window)
  );
}

export interface RemedyFile {
  path: string;
  source: string;
}

/** Invoke a Rust command. No-op (throws) outside Tauri. */
export async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  if (!isTauri()) throw new Error('Tauri host not available.');
  const { invoke: tauriInvoke } = await import('@tauri-apps/api/core');
  return tauriInvoke<T>(cmd, args);
}

/** Listen for a host event; returns an unlisten() fn. No-op outside Tauri. */
export async function listen<T>(
  event: string,
  cb: (payload: T) => void,
): Promise<() => void> {
  if (!isTauri()) return () => {};
  const { listen: tauriListen } = await import('@tauri-apps/api/event');
  const unlisten = await tauriListen<T>(event, (e) => cb(e.payload));
  return unlisten;
}

/** Native folder picker. Returns the absolute path or null if cancelled. */
export async function pickFolder(): Promise<string | null> {
  if (!isTauri()) return null;
  const { open } = await import('@tauri-apps/plugin-dialog');
  const result = await open({ directory: true, multiple: false });
  return typeof result === 'string' ? result : null;
}

/** Read a .remedy file from disk through the host. */
export async function readRemedy(path: string): Promise<RemedyFile> {
  return invoke<RemedyFile>('read_remedy', { path });
}

/** Start watching `folder` for .remedy changes. */
export async function startWatching(folder: string): Promise<void> {
  await invoke<void>('start_watching', { folder });
}

export async function stopWatching(): Promise<void> {
  await invoke<void>('stop_watching');
}
